import { useEffect, useRef } from 'react';

const GOOGLE_CLIENT_ID = '240970820671-pdeepko82uhhb0bmrqehkp4qinct4l6l.apps.googleusercontent.com';
const SCRIPT_SRC = 'https://accounts.google.com/gsi/client';

interface Props {
    onCredential: (credential: string) => void;
    text?: 'signin_with' | 'signup_with' | 'continue_with';
}

declare global {
    interface Window {
        google?: {
            accounts: {
                id: {
                    initialize: (config: { client_id: string; callback: (resp: { credential: string }) => void }) => void;
                    renderButton: (el: HTMLElement, options: Record<string, unknown>) => void;
                };
            };
        };
    }
}

let scriptLoading: Promise<void> | null = null;

function loadGsi(): Promise<void> {
    if (window.google?.accounts?.id) return Promise.resolve();
    if (scriptLoading) return scriptLoading;
    scriptLoading = new Promise<void>((resolve, reject) => {
        const existing = document.querySelector(`script[src="${SCRIPT_SRC}"]`);
        if (existing) {
            existing.addEventListener('load', () => resolve());
            existing.addEventListener('error', () => reject(new Error('GSI script failed')));
            return;
        }
        const s = document.createElement('script');
        s.src = SCRIPT_SRC;
        s.async = true;
        s.defer = true;
        s.onload = () => resolve();
        s.onerror = () => reject(new Error('GSI script failed'));
        document.head.appendChild(s);
    });
    return scriptLoading;
}

export function GoogleButton({ onCredential, text = 'signin_with' }: Props) {
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        let cancelled = false;
        loadGsi()
            .then(() => {
                if (cancelled || !window.google?.accounts?.id || !ref.current) return;
                window.google.accounts.id.initialize({
                    client_id: GOOGLE_CLIENT_ID,
                    callback: (resp) => onCredential(resp.credential),
                });
                window.google.accounts.id.renderButton(ref.current, {
                    type: 'standard',
                    theme: 'outline',
                    size: 'large',
                    text,
                    shape: 'rectangular',
                    logo_alignment: 'left',
                    width: ref.current.clientWidth || 280,
                });
            })
            .catch(() => { /* silenciar — el usuario puede usar password */ });
        return () => { cancelled = true; };
    }, [onCredential, text]);

    return <div ref={ref} className="flex justify-center" />;
}
